import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { listUserDocuments, getDocumentUrl } from '../services/documentService';
import type { Document } from '../services/documentService';

interface PreviewSection {
  title: string;
  type: 'grid' | 'table';
  data: any;
}

const ArchivedSummary: React.FC = () => {
  const { user } = useAuth();
  const [archivedDocs, setArchivedDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewSections, setPreviewSections] = useState<PreviewSection[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDocName, setPreviewDocName] = useState('');

  useEffect(() => {
    loadArchived();
  }, []);

  const loadArchived = async () => {
    setLoading(true);
    setError('');
    try {
      const docs = await listUserDocuments(user?.userId || '', undefined, 'health-summary');
      const xlsxDocs = docs
        .filter((d) => d.name.toLowerCase().endsWith('.xlsx'))
        .sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());

      // Skip the most recent one — that's shown on the Health Summary tab
      setArchivedDocs(xlsxDocs.slice(1));
    } catch (err: any) {
      setError(err.message || 'Failed to load archived summaries');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (doc: Document) => {
    setPreviewLoading(true);
    setPreviewSections(null);
    setPreviewDocName(doc.name);
    try {
      const url = await getDocumentUrl(doc.key);
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const XLSX = await import('xlsx');

      const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });

      const findSheet = (wb: any, ...names: string[]) => {
        for (const n of wb.SheetNames) {
          const norm = n.trim().toLowerCase();
          for (const c of names) {
            if (norm === c.toLowerCase()) return wb.Sheets[n];
          }
        }
        return null;
      };

      const parseFieldValue = (sheet: any) => {
        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (rows.length < 2) return [];
        const headers = rows[0].map((h: any) => String(h).trim().toLowerCase());
        const fi = headers.indexOf('field');
        const vi = headers.indexOf('value');
        if (fi === -1 || vi === -1) return [];
        return rows.slice(1).filter((r) => r[fi]).map((r) => ({
          field: String(r[fi] ?? '').trim(),
          value: String(r[vi] ?? '').trim(),
        }));
      };

      const parseTable = (sheet: any) => {
        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (rows.length < 2) return { headers: [] as string[], rows: [] as string[][] };
        const headers = rows[0].map((h: any) => String(h ?? '').trim());
        const dataRows = rows.slice(1)
          .filter((r) => r.some((c: any) => c != null && String(c).trim()))
          .map((r) => headers.map((_, i) => String(r[i] ?? '').trim()));
        return { headers, rows: dataRows };
      };

      const sections: PreviewSection[] = [];

      // Field/Value sheets
      const fvSheets = [
        { names: ['Personal Identification', 'Personal Info'], title: 'Personal Identification' },
        { names: ['Demographics'], title: 'Demographics' },
      ];
      for (const { names, title } of fvSheets) {
        const sheet = findSheet(workbook, ...names);
        if (sheet) {
          const fv = parseFieldValue(sheet);
          if (fv.length) sections.push({ title, type: 'grid', data: fv });
        }
      }

      // Table sheets
      const tableSheets = [
        { names: ['Emergency Contacts'], title: 'Emergency Contacts' },
        { names: ['Family Doctor'], title: 'Family Doctor & Pharmacy' },
        { names: ['Active Medical Conditions', 'Conditions'], title: 'Active Medical Conditions' },
        { names: ['Allergies & Adverse Reaction', 'Allergies'], title: 'Allergies & Adverse Reactions' },
        { names: ['Medications'], title: 'Medications' },
        { names: ['Vaccine History'], title: 'Vaccine History' },
        { names: ['Diagnostic History'], title: 'Diagnostic History' },
        { names: ['Surgical Hx & Hospitalization'], title: 'Surgical History & Hospitalization' },
        { names: ['Travel Hx'], title: 'Travel History' },
      ];
      for (const { names, title } of tableSheets) {
        const sheet = findSheet(workbook, ...names);
        if (sheet) {
          const t = parseTable(sheet);
          if (t.rows.length) sections.push({ title, type: 'table', data: t });
        }
      }

      // Insurance (field/value)
      const insuranceSheet = findSheet(workbook, 'Insurance & Care Planning');
      if (insuranceSheet) {
        const fv = parseFieldValue(insuranceSheet);
        if (fv.length) sections.push({ title: 'Insurance & Care Planning', type: 'grid', data: fv });
      }

      // More tables
      const moreTableSheets = [
        { names: ['Family Hx', 'Family History'], title: 'Family History' },
        { names: ['Care Team'], title: 'Care Team' },
      ];
      for (const { names, title } of moreTableSheets) {
        const sheet = findSheet(workbook, ...names);
        if (sheet) {
          const t = parseTable(sheet);
          if (t.rows.length) sections.push({ title, type: 'table', data: t });
        }
      }

      setPreviewSections(sections);
    } catch (err: any) {
      setError(err.message || 'Failed to load summary preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="archived-summary">
      <div className="page-header">
        <h1>Archived Summaries</h1>
        <p>View previous versions of your health summary.</p>
      </div>

      <div className="content-card">
        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <p className="loading-text">Loading archived summaries...</p>
        ) : archivedDocs.length > 0 ? (
          <>
            <div className="archived-list">
              {archivedDocs.map((doc) => (
                <div key={doc.key} className="archived-item">
                  <div className="archived-info">
                    <span className="archived-name">{doc.name}</span>
                    <span className="archived-meta">
                      {new Date(doc.lastModified).toLocaleDateString('en-CA', {
                        year: 'numeric', month: 'long', day: 'numeric',
                      })}
                    </span>
                  </div>
                  <button
                    className="archived-view-btn"
                    onClick={() => handlePreview(doc)}
                  >
                    View
                  </button>
                </div>
              ))}
            </div>

            {previewLoading && (
              <p className="loading-text">Loading summary...</p>
            )}

            {previewSections && (
              <div className="archived-preview">
                <div className="archived-preview-header">
                  <h2>{previewDocName}</h2>
                  <button className="archived-close-btn" onClick={() => setPreviewSections(null)}>
                    Close
                  </button>
                </div>

                {previewSections.map((section, i) => (
                  <div key={i} className="hs-section">
                    <h3 className="hs-section-title">{section.title}</h3>
                    {section.type === 'grid' ? (
                      <div className="hs-personal-grid">
                        {section.data.map((item: any, j: number) => (
                          <div key={j} className="hs-personal-card">
                            <span className="hs-field-label">{item.field}</span>
                            <span className="hs-field-value">{item.value || 'N/A'}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="hs-table-wrapper">
                        <table className="hs-table">
                          <thead>
                            <tr>
                              {section.data.headers.map((h: string, j: number) => (
                                <th key={j}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {section.data.rows.map((row: string[], j: number) => (
                              <tr key={j}>
                                {row.map((cell: string, k: number) => (
                                  <td key={k}>{cell || 'N/A'}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="empty-state">No archived summaries available. Older versions of your health summary will appear here.</p>
        )}
      </div>

      <style>{`
        .archived-summary p {
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 1rem;
        }

        /* Archived list */
        .archived-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        .archived-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.25rem;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          transition: border-color 0.15s;
        }
        .archived-item:hover {
          border-color: var(--primary);
        }
        .archived-info {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }
        .archived-name {
          color: var(--text);
          font-weight: 600;
          font-size: 0.95rem;
        }
        .archived-meta {
          color: var(--text-light);
          font-size: 0.85rem;
        }
        .archived-view-btn {
          padding: 0.5rem 1.25rem;
          background: transparent;
          border: 1.5px solid var(--border);
          border-radius: var(--radius-sm);
          color: var(--text-muted);
          font-size: 0.9rem;
          font-weight: 700;
          font-family: var(--font-body);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .archived-view-btn:hover {
          border-color: var(--primary);
          color: var(--primary);
          background: var(--primary-bg);
        }

        /* Preview */
        .archived-preview {
          border-top: 1px solid var(--border);
          padding-top: 1.5rem;
        }
        .archived-preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        .archived-preview-header h2 {
          color: var(--text);
          font-family: var(--font-display);
          font-size: 1.15rem;
          margin: 0;
        }
        .archived-close-btn {
          padding: 0.4rem 1rem;
          background: transparent;
          border: 1.5px solid var(--border);
          border-radius: var(--radius-sm);
          color: var(--text-muted);
          font-size: 0.85rem;
          font-weight: 700;
          font-family: var(--font-body);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .archived-close-btn:hover {
          border-color: var(--primary);
          color: var(--primary);
        }

        /* Reuse hs- styles (light theme) */
        .hs-section {
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          padding: 1.5rem;
          margin-bottom: 1rem;
        }
        .hs-section-title {
          color: var(--text);
          font-family: var(--font-display);
          font-size: 1.1rem;
          font-weight: 700;
          margin: 0 0 1rem 0;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid var(--border);
        }
        .hs-personal-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 0.75rem;
        }
        .hs-personal-card {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          padding: 0.75rem 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .hs-field-label {
          color: var(--text-muted);
          font-size: 0.8rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .hs-field-value {
          color: var(--text);
          font-size: 1rem;
          font-weight: 500;
        }
        .hs-table-wrapper {
          overflow-x: auto;
        }
        .hs-table {
          width: 100%;
          border-collapse: collapse;
        }
        .hs-table th {
          text-align: left;
          color: var(--text-muted);
          font-size: 0.8rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 12px 16px;
          border-bottom: 2px solid var(--border);
          background: var(--bg);
        }
        .hs-table td {
          color: var(--text);
          padding: 14px 16px;
          border-bottom: 1px solid var(--border);
          font-size: 0.95rem;
        }
        .hs-table tbody tr:last-child td {
          border-bottom: none;
        }
        .hs-table tbody tr:hover {
          background: var(--primary-bg);
        }

        @media (max-width: 768px) {
          .hs-table th,
          .hs-table td {
            padding: 0.5rem;
            font-size: 0.85rem;
          }
          .archived-item {
            flex-direction: column;
            gap: 0.75rem;
            align-items: flex-start;
          }
          .archived-view-btn {
            width: 100%;
            text-align: center;
          }
        }

        @media (max-width: 600px) {
          .hs-personal-grid {
            grid-template-columns: 1fr;
          }
          .hs-section {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ArchivedSummary;
